import React, { useEffect } from "react";
import { TFunction } from "i18next";

import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { HAS_NO_LOYALTY } from "@constants/index";

import {
    FormValues,
    FormFlags,
    FormErrors,
    FormStateApi,
    INITIAL_VALUES,
    INITIAL_FLAGS,
    INITIAL_ERRORS,
} from "./types";

type Params = {
    // En mode "add", le caller passe un objet vide (sans Id) — d'où Partial.
    // L'index signature de FidelityRow couvre les bracket accesses i18n
    // (`dataFidelity[t("Display name")]`, etc.).
    dataFidelity: Partial<FidelityRow>;
    t: TFunction;
    saleBO: string[];
    isConsult: boolean;
    isAddNewFidelityButtonClicked: boolean;
    isAddFidelityClicked: boolean;
    setFidelityChoiceIndex: (idx: number) => void;
};

export function useFidelityFormState(
    params: Params
): FormStateApi & {
    title: string;
} {
    const {
        dataFidelity,
        t,
        saleBO,
        isConsult,
        isAddNewFidelityButtonClicked,
        isAddFidelityClicked,
        setFidelityChoiceIndex,
    } = params;

    const [title, setTitle] = React.useState<string>("");
    const [values, setValuesState] = React.useState<FormValues>(INITIAL_VALUES);
    const [flags, setFlagsState] = React.useState<FormFlags>(INITIAL_FLAGS);
    const [errors, setErrorsState] = React.useState<FormErrors>(INITIAL_ERRORS);

    const setValue = React.useCallback(
        <K extends keyof FormValues>(key: K, v: FormValues[K]) => {
            setValuesState((prev) => ({ ...prev, [key]: v }));
        },
        []
    );

    const setFlag = React.useCallback(
        <K extends keyof FormFlags>(key: K, v: FormFlags[K]) => {
            setFlagsState((prev) => ({ ...prev, [key]: v }));
        },
        []
    );

    const setError = React.useCallback(
        <K extends keyof FormErrors>(key: K, v: FormErrors[K]) => {
            setErrorsState((prev) => ({ ...prev, [key]: v }));
        },
        []
    );

    const resetErrors = React.useCallback(() => {
        setErrorsState(INITIAL_ERRORS);
    }, []);

    // Invariant : `dataFidelity` est une valeur de `useState` côté parent
    // (FidelityFranchise/index.tsx:83 `[fidelity, setFidelity]`), donc sa
    // référence ne change qu'aux transitions explicites (consult/edit/reset).
    // L'effet ne se relance pas à chaque render et n'écrase pas les saisies.
    // Si un futur caller construit l'objet en ligne, dépendre de
    // `dataFidelity.Id` + lire le reste via useRef.
    useEffect(() => {
        if (dataFidelity.Id !== undefined) {
            setTitle(t(isConsult ? "View fidelity" : "Modify fidelity"));
            setValuesState({
                designation: dataFidelity[t("Display name")] ?? "",
                choiceFidelity: dataFidelity[t("Loyalty")] ?? "",
                numberFidelityPoints:
                    dataFidelity.number_fidelity_points ?? "100",
                moneyCorrespondence: dataFidelity.moneyCorrespondence ?? "1",
                authenticationMode:
                    typeof dataFidelity.authentication_mode === "string"
                        ? dataFidelity.authentication_mode.split(",")
                        : [],
                sortedVisibility: (dataFidelity.sortedVisibility ?? []).filter(
                    (el: any) =>
                        saleBO.includes(el.designation) ||
                        saleBO.includes(el.designation?.[0])
                ),
                remark: dataFidelity[t("Remark")] ?? "",
                login: dataFidelity.Login ?? "",
                password: dataFidelity[t("Password")] ?? "",
                user: dataFidelity.user ?? "",
                dashboardLink: dataFidelity[t("Dashboard Link")] ?? "",
                apiKey: dataFidelity["API key"] ?? "",
                auresKonnectGroup: dataFidelity[t("Group")] ?? "",
            });
            setFlagsState({
                isCentralizationActive: dataFidelity[t("State")] ?? false,
                isVisibiliteActive:
                    dataFidelity[t("isVisibiliteActive")] ?? false,
            });
            setFidelityChoiceIndex(
                dataFidelity.fidelityChoiceIndex ?? HAS_NO_LOYALTY
            );
        } else {
            setTitle(t("Add fidelity"));
            setValuesState(INITIAL_VALUES);
            setFlagsState(INITIAL_FLAGS);
            setErrorsState(INITIAL_ERRORS);
        }
    }, [
        dataFidelity,
        t,
        isConsult,
        saleBO,
        setFidelityChoiceIndex,
        isAddNewFidelityButtonClicked,
        isAddFidelityClicked,
    ]);

    useEffect(() => {
        clearData();
        clearMultiCustomSelectionData();
    }, [isAddFidelityClicked, dataFidelity.Id]);

    // Clear the 3 "required field" error flags only when the edited record's
    // identity changes (consult/edit a different row). On the same row, errors
    // persist so the user keeps seeing the red highlights they triggered via
    // handleValidate. On "Add" (Id undefined → undefined), the first effect's
    // `else` branch already resets ALL errors via INITIAL_ERRORS — no overlap.
    useEffect(() => {
        setErrorsState((prev) => ({
            ...prev,
            designation: false,
            password: false,
            login: false,
        }));
    }, [dataFidelity.Id]);

    return {
        title,
        values,
        flags,
        errors,
        setValue,
        setFlag,
        setError,
        resetErrors,
    };
}
